/**
 * ConnectorSheet
 *
 * Right-side sheet for viewing and configuring any connector type.
 * Replaces ConnectorConfig + ConnectorSettings pages with inline config.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { BrandIcon, BRAND_ICONS } from '@/components/ui/brand-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Alert01Icon,
  ArrowDown01Icon,
  CheckmarkCircle01Icon,
  Copy01Icon,
  Delete01Icon,
  LinkSquare01Icon,
  Tick01Icon,
} from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  getRevenueConfig,
  saveRevenueConfig,
  testRevenueConfig,
  disconnectRevenueConfig,
  toggleSiteRevenueDisplay,
  getFoxyWebhookInfo,
  getJotformWebhookInfo,
  getTypeformWebhookInfo,
  saveTypeformConfig,
  testTypeformConfig,
  getTallyWebhookInfo,
  getWebflowWebhookInfo,
  saveWebflowConfig,
  testWebflowConfig,
} from '@/lib/api/api'
import { updateGoogleAdsSelectedAccounts } from '@/lib/api/oauth'
import type { RevenueSetup } from '@/lib/types/workspace'
import type { ConnectorConfig, ConnectorField, OAuthConnection } from '@/lib/types/connector'
import {
  STRIPE_CONNECTOR_CONFIG,
  FOXY_CONNECTOR_CONFIG,
  JOTFORM_CONNECTOR_CONFIG,
  TYPEFORM_CONNECTOR_CONFIG,
  TALLY_CONNECTOR_CONFIG,
  WEBFLOW_CONNECTOR_CONFIG,
} from '@/lib/config/connectors'
import { getConnectorErrorMessage, formatProviderName } from '@/lib/utils/connectorErrors'

// ── Types ──

type CredentialConnectorType = 'stripe' | 'foxy' | 'jotform' | 'typeform' | 'tally' | 'webflow'
type OAuthConnectorType = 'ga4' | 'meta' | 'gsc' | 'google-ads'
type NativeConnectorType = 'humblytics-script'

export type SheetConnectorType = CredentialConnectorType | OAuthConnectorType | NativeConnectorType

interface ConnectorSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  connectorType: SheetConnectorType | null
  propertyId: string
  token: string
  siteDomain?: string
  /** Ads and revenue connectors cover every site in this workspace. */
  workspaceName?: string
  /** For OAuth connectors: the current connection (if any) */
  oauthConnection?: OAuthConnection | null
  /** Callbacks for OAuth actions */
  onOAuthConnect?: (type: OAuthConnectorType) => void
  onOAuthReconnect?: (type: OAuthConnectorType) => void
  onOAuthDisconnect?: (connection: OAuthConnection) => void
  onOAuthSync?: (connection: OAuthConnection) => void
  /** Callback after credential connector is saved/disconnected */
  onConfigChanged?: () => void
  /** Revenue display toggle state */
  showRevenueAttribution?: boolean
}

// ── Constants ──

const FORMS_TYPES: CredentialConnectorType[] = ['jotform', 'typeform', 'tally', 'webflow']
const WEBHOOK_ONLY: CredentialConnectorType[] = ['jotform', 'tally']

const CONNECTOR_CONFIGS: Record<CredentialConnectorType, Omit<ConnectorConfig, 'status' | 'enabled'>> = {
  stripe: STRIPE_CONNECTOR_CONFIG,
  foxy: FOXY_CONNECTOR_CONFIG,
  jotform: JOTFORM_CONNECTOR_CONFIG,
  typeform: TYPEFORM_CONNECTOR_CONFIG,
  tally: TALLY_CONNECTOR_CONFIG,
  webflow: WEBFLOW_CONNECTOR_CONFIG,
}

const OAUTH_NAMES: Record<OAuthConnectorType, string> = {
  ga4: 'Google Analytics 4',
  meta: 'Meta Ads',
  gsc: 'Google Search Console',
  'google-ads': 'Google Ads',
}

const OAUTH_BRANDS: Record<OAuthConnectorType, keyof typeof BRAND_ICONS> = {
  ga4: 'googleAnalytics',
  meta: 'meta',
  gsc: 'searchConsole',
  'google-ads': 'googleAds',
}

// ── Helpers ──

function isCredentialType(type: SheetConnectorType): type is CredentialConnectorType {
  return type in CONNECTOR_CONFIGS
}

function isOAuthType(type: SheetConnectorType): type is OAuthConnectorType {
  return type in OAUTH_NAMES
}

function formatLastSync(lastSyncAt?: string): string {
  if (!lastSyncAt) return 'Never synced'
  const diffMs = Date.now() - new Date(lastSyncAt).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(diffMs / 3600000)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(diffMs / 86400000)}d ago`
}

// ── Component ──

export function ConnectorSheet({
  open,
  onOpenChange,
  connectorType,
  propertyId,
  token,
  siteDomain,
  workspaceName,
  oauthConnection,
  onOAuthConnect,
  onOAuthReconnect,
  onOAuthDisconnect,
  onOAuthSync,
  onConfigChanged,
  showRevenueAttribution: initialShowRevenue,
}: ConnectorSheetProps) {
  if (!connectorType) return null
  const workspaceLabel = workspaceName || 'this workspace'

  if (connectorType === 'humblytics-script') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <BrandIcon brand="humblytics" className="w-7 h-7" />
              <div>
                <SheetTitle>Humblytics script</SheetTitle>
                <SheetDescription>Your analytics tracking script</SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/20 border border-border/30">
              <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-success dark:text-success shrink-0" />
              <p className="text-sm text-success dark:text-success">Script is installed and tracking</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                The Humblytics script is automatically active on all pages where it's installed.
                It tracks pageviews, clicks, scroll depth, form submissions, and sessions.
              </p>
              <p className="text-sm text-muted-foreground">
                Heatmaps, A/B experiments, and funnels are enabled through this script.
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Property</Label>
              <p className="text-sm font-medium">{siteDomain || 'Your site'}</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  if (isOAuthType(connectorType)) {
    return (
      <OAuthSheet
        open={open}
        onOpenChange={onOpenChange}
        type={connectorType}
        connection={oauthConnection}
        token={token}
        workspaceLabel={workspaceLabel}
        onConnect={onOAuthConnect}
        onReconnect={onOAuthReconnect}
        onDisconnect={onOAuthDisconnect}
        onSync={onOAuthSync}
        onAccountsChanged={onConfigChanged}
      />
    )
  }

  if (isCredentialType(connectorType)) {
    return (
      <CredentialSheet
        open={open}
        onOpenChange={onOpenChange}
        type={connectorType}
        propertyId={propertyId}
        token={token}
        workspaceLabel={workspaceLabel}
        onConfigChanged={onConfigChanged}
        initialShowRevenue={initialShowRevenue}
      />
    )
  }

  return null
}

// ── OAuth Sheet ──

function OAuthSheet({
  open,
  onOpenChange,
  type,
  connection,
  token,
  workspaceLabel,
  onConnect,
  onReconnect,
  onDisconnect,
  onSync,
  onAccountsChanged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: OAuthConnectorType
  connection?: OAuthConnection | null
  token?: string
  workspaceLabel: string
  onConnect?: (type: OAuthConnectorType) => void
  onReconnect?: (type: OAuthConnectorType) => void
  onDisconnect?: (conn: OAuthConnection) => void
  onSync?: (conn: OAuthConnection) => void
  onAccountsChanged?: () => void
}) {
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [savingAccounts, setSavingAccounts] = useState(false)
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set())
  const name = OAUTH_NAMES[type]
  const brand = OAUTH_BRANDS[type]
  const isConnected = connection?.status === 'connected'
  const needsReconnect = connection?.status === 'expired' || connection?.status === 'error'

  const adAccounts = connection?.metadata?.adAccounts ?? []
  const isGoogleAds = type === 'google-ads'
  const isAdsType = type === 'meta' || isGoogleAds

  // Initialise checkbox state whenever the connection/sheet changes
  useEffect(() => {
    if (!open || !isGoogleAds) return
    const initial = new Set(
      adAccounts.filter(a => a.selected !== false).map(a => String(a.id))
    )
    setSelectedAccountIds(initial)
  }, [open, connection?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAccount = (id: string) => {
    setSelectedAccountIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSaveAccounts = async () => {
    if (!connection?.id || !token) return
    setSavingAccounts(true)
    try {
      await updateGoogleAdsSelectedAccounts(connection.id, Array.from(selectedAccountIds), token)
      toast.success('Ad account selection saved')
      onAccountsChanged?.()
    } catch {
      toast.error('Failed to save account selection')
    } finally {
      setSavingAccounts(false)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <BrandIcon brand={brand} className="w-7 h-7" />
              <div>
                <SheetTitle>{name}</SheetTitle>
                <SheetDescription>
                  {isAdsType
                    ? `For ${workspaceLabel}. Covers every site in this workspace.`
                    : isConnected
                      ? `Connected via OAuth`
                      : `Connect your ${name} account`
                  }
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            {!connection ? (
              /* Not connected */
              <>
                <p className="text-sm text-muted-foreground">
                  {isAdsType
                    ? `Connect the ${name} account for ${workspaceLabel}. Its spend is attributed across every site in this workspace. A different ad account needs its own workspace.`
                    : `Connect your ${name} account to import data into Humblytics.`}
                  {' '}You'll be redirected to authorize access.
                </p>
                <Button
                  className="w-full"
                  onClick={() => { onConnect?.(type); onOpenChange(false) }}
                >
                  Connect {name}
                </Button>
              </>
            ) : needsReconnect ? (
              /* Expired / Error */
              <>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/5 border border-warning/20">
                  <HugeiconsIcon icon={Alert01Icon} size={16} className="text-warning shrink-0" />
                  <p className="text-sm text-warning dark:text-warning">
                    {connection.status === 'expired' ? 'Token expired. Reconnect to resume syncing' : 'Sync error. Reconnect to resume syncing'}
                  </p>
                </div>
                {connection.connectedEmail && (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground">Account</Label>
                    <p className="text-sm">{connection.connectedEmail}</p>
                  </div>
                )}
                <Button
                  className="w-full"
                  onClick={() => { onReconnect?.(type); onOpenChange(false) }}
                >
                  Reconnect
                </Button>
              </>
            ) : (
              /* Connected */
              <>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/20 border border-border/30">
                  <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-success dark:text-success shrink-0" />
                  <p className="text-sm text-success dark:text-success">Connected and syncing</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {isAdsType && (
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs font-medium text-muted-foreground">Workspace</Label>
                      <p className="text-sm truncate">{workspaceLabel}</p>
                    </div>
                  )}
                  {connection.connectedEmail && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-muted-foreground">Account</Label>
                      <p className="text-sm truncate">{connection.connectedEmail}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground">Last sync</Label>
                    <p className="text-sm">{formatLastSync(connection.lastSyncAt)}</p>
                  </div>
                  {isGoogleAds && adAccounts.length > 0 && (
                    <div className="col-span-2 space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">Ad Accounts to sync</Label>
                      <div className="space-y-1 rounded-md border border-border/30 p-2">
                        {adAccounts.map(a => (
                          <label key={a.id} className="flex items-center gap-2.5 px-1 py-1 rounded cursor-pointer hover:bg-muted/40 transition-colors">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-primary"
                              checked={selectedAccountIds.has(String(a.id))}
                              onChange={() => toggleAccount(String(a.id))}
                            />
                            <span className="text-sm truncate">{a.name || a.id}</span>
                          </label>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        disabled={savingAccounts}
                        onClick={handleSaveAccounts}
                      >
                        {savingAccounts ? 'Saving…' : 'Save selection'}
                      </Button>
                    </div>
                  )}
                  {!isGoogleAds && adAccounts.some(a => a.selected) && (
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs font-medium text-muted-foreground">Ad accounts</Label>
                      {adAccounts.filter(a => a.selected).map(a => (
                        <p key={a.id} className="text-sm truncate">
                          {a.name || a.id}
                          <span className="text-xs text-muted-foreground"> · {a.id}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  {connection.metadata?.ga4PropertyName && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-muted-foreground">Property</Label>
                      <p className="text-sm truncate">{connection.metadata.ga4PropertyName}</p>
                    </div>
                  )}
                </div>

                {onSync && (
                  <>
                    <Separator />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => onSync(connection)}
                    >
                      Sync now
                    </Button>
                  </>
                )}

                <Separator />

                {/* Disconnect */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors">
                    <HugeiconsIcon icon={ArrowDown01Icon} size={12} />
                    Disconnect
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => setDisconnectOpen(true)}
                    >
                      <HugeiconsIcon icon={Delete01Icon} size={14} className="mr-1.5" />
                      Disconnect {name}
                    </Button>
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {name} connection{isAdsType ? ` from ${workspaceLabel}` : ''}. You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (connection) onDisconnect?.(connection)
                setDisconnectOpen(false)
                onOpenChange(false)
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ── Credential Sheet ──

function CredentialSheet({
  open,
  onOpenChange,
  type,
  propertyId,
  token,
  workspaceLabel,
  onConfigChanged,
  initialShowRevenue,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: CredentialConnectorType
  propertyId: string
  token: string
  workspaceLabel: string
  onConfigChanged?: () => void
  initialShowRevenue?: boolean
}) {
  const config = CONNECTOR_CONFIGS[type]
  const isFormsType = FORMS_TYPES.includes(type)
  const isWebhookOnlyType = WEBHOOK_ONLY.includes(type)
  const isRevenueType = type === 'stripe' || type === 'foxy'

  // State
  const [values, setValues] = useState<Record<string, string>>({})
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})
  const [revenueConfig, setRevenueConfig] = useState<RevenueSetup | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(!isFormsType)
  const [isLoading, setIsLoading] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [error, setError] = useState('')
  const [testSuccess, setTestSuccess] = useState(false)
  const [showRevenue, setShowRevenue] = useState(initialShowRevenue ?? true)
  const [isTogglingRevenue, setIsTogglingRevenue] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [setupOpen, setSetupOpen] = useState(true)

  // Load revenue config for revenue connectors
  const loadRevenueConfig = useCallback(async () => {
    if (!propertyId || !token || isFormsType) return
    setIsLoadingConfig(true)
    try {
      const response = await getRevenueConfig(propertyId, token)
      const setup = response?.revenueSetup
        ?? ((response?.stripe !== undefined || response?.foxy !== undefined) ? response : null)
      setRevenueConfig(setup)
      setShowRevenue(response?.showRevenueAttribution ?? true)
    } catch {
      // ignore
    } finally {
      setIsLoadingConfig(false)
    }
  }, [propertyId, token, isFormsType])

  useEffect(() => {
    if (open && !isFormsType) loadRevenueConfig()
  }, [open, loadRevenueConfig, isFormsType])

  // Load dynamic fields (webhook URLs)
  useEffect(() => {
    if (!open || !propertyId || !token) return

    const webhookLoaders: Partial<Record<CredentialConnectorType, () => Promise<{ webhookUrl?: string }>>> = {
      foxy: () => getFoxyWebhookInfo(propertyId, token),
      jotform: () => getJotformWebhookInfo(propertyId, token),
      typeform: () => getTypeformWebhookInfo(propertyId, token),
      tally: () => getTallyWebhookInfo(propertyId, token),
      webflow: () => getWebflowWebhookInfo(propertyId, token),
    }

    const loader = webhookLoaders[type]
    if (loader) {
      setLoadingFields(prev => ({ ...prev, webhookUrl: true }))
      loader()
        .then(info => {
          if (info?.webhookUrl) {
            setValues(prev => ({ ...prev, webhookUrl: info.webhookUrl! }))
          }
        })
        .catch(() => {})
        .finally(() => setLoadingFields(prev => ({ ...prev, webhookUrl: false })))
    }
  }, [open, type, propertyId, token])

  // Initialize form values
  useEffect(() => {
    if (!open) {
      setValues({})
      setError('')
      setTestSuccess(false)
      return
    }
    if (config && revenueConfig) {
      const initial: Record<string, string> = {}
      config.fields.forEach(field => {
        if (type === 'foxy' && field.id === 'storeUrl' && revenueConfig.foxy?.storeUrl) {
          initial[field.id] = revenueConfig.foxy.storeUrl
        } else {
          initial[field.id] = ''
        }
      })
      setValues(prev => ({ ...prev, ...initial }))
    }
  }, [open, config, revenueConfig, type])

  const isConnected = useMemo(() => {
    if (isWebhookOnlyType) return true
    if (isFormsType) return false
    if (!revenueConfig) return false
    if (type === 'stripe') return !!revenueConfig.stripe?.configured
    if (type === 'foxy') return !!revenueConfig.foxy?.configured
    return false
  }, [revenueConfig, type, isFormsType, isWebhookOnlyType])

  // Close setup instructions by default when already connected
  useEffect(() => {
    setSetupOpen(!isConnected)
  }, [isConnected])

  const handleCopy = (value: string, fieldId: string) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => {
        const ta = document.createElement('textarea')
        ta.value = value
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      })
    } else {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopiedField(fieldId)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopiedField(null), 2000)
  }

  const handleChange = (fieldId: string, value: string) => {
    setValues(prev => ({ ...prev, [fieldId]: value }))
    setError('')
    setTestSuccess(false)
  }

  const validate = (): string | null => {
    for (const field of config.fields) {
      if (field.required && !values[field.id]?.trim()) return `${field.label} is required`
      if (field.pattern && values[field.id]) {
        // Trimmed: a pasted key often carries a trailing space or newline.
        if (!new RegExp(field.pattern).test(values[field.id].trim())) return field.patternError || `Invalid ${field.label.toLowerCase()}`
      }
    }
    return null
  }

  const handleSave = async () => {
    if (isWebhookOnlyType) return
    const err = validate()
    if (err) { setError(err); return }

    setIsLoading(true)
    setError('')
    try {
      if (type === 'stripe') {
        await saveRevenueConfig(propertyId, 'stripe', { method: 'api', apiKey: values.apiKey?.trim() }, token)
      } else if (type === 'foxy') {
        await saveRevenueConfig(propertyId, 'foxy', { storeUrl: values.storeUrl, encryptionKey: values.encryptionKey }, token)
      } else if (type === 'typeform') {
        await saveTypeformConfig(propertyId, values.apiToken, token)
      } else if (type === 'webflow') {
        await saveWebflowConfig(propertyId, values.siteId, values.apiToken, token)
      }
      toast.success(`${config.name} ${isConnected ? 'updated' : 'connected'} successfully`)
      onConfigChanged?.()
      if (!isFormsType) await loadRevenueConfig()
    } catch (err) {
      setError(getConnectorErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleTest = async () => {
    if (!config.supportsTest) return
    setIsTesting(true)
    setError('')
    setTestSuccess(false)
    try {
      let result: { success: boolean; message?: string }
      if (type === 'typeform') result = await testTypeformConfig(propertyId, token)
      else if (type === 'webflow') result = await testWebflowConfig(propertyId, token)
      else if (type === 'stripe') {
        const apiKey = values.apiKey?.trim()
        result = await testRevenueConfig(
          propertyId,
          'stripe',
          token,
          apiKey ? { method: 'api', apiKey } : undefined
        )
      } else if (type === 'foxy') {
        const storeUrl = values.storeUrl?.trim()
        const encryptionKey = values.encryptionKey?.trim()
        result = await testRevenueConfig(
          propertyId,
          'foxy',
          token,
          storeUrl || encryptionKey ? { storeUrl, encryptionKey } : undefined
        )
      } else {
        result = await testRevenueConfig(propertyId, type, token)
      }
      if (result.success) { setTestSuccess(true); toast.success('Connection test passed') }
      else setError(result.message || 'Test failed')
    } catch (err) {
      setError(getConnectorErrorMessage(err))
    } finally {
      setIsTesting(false)
    }
  }

  const handleDisconnect = async () => {
    setIsDisconnecting(true)
    try {
      await disconnectRevenueConfig(propertyId, type, token)
      toast.success(`${formatProviderName(type)} disconnected`)
      onConfigChanged?.()
      setDisconnectOpen(false)
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to disconnect', { description: getConnectorErrorMessage(err) })
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleToggleRevenue = async (enabled: boolean) => {
    setIsTogglingRevenue(true)
    try {
      await toggleSiteRevenueDisplay(propertyId, enabled, token)
      setShowRevenue(enabled)
      toast.success(enabled ? 'Revenue display enabled' : 'Revenue display disabled')
      onConfigChanged?.()
    } catch {
      toast.error('Failed to update setting')
    } finally {
      setIsTogglingRevenue(false)
    }
  }

  const hasChanges = !isWebhookOnlyType && config.fields.some(f => f.type !== 'readonly' && values[f.id]?.trim())
  const brandColor = BRAND_ICONS[config.brandKey]?.color

  // Field renderer
  const renderField = (field: ConnectorField) => {
    const isLoadingField = loadingFields[field.id]
    const fieldValue = values[field.id] || ''

    return (
      <div key={field.id} className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={field.id} className="text-xs font-medium text-muted-foreground">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          {field.externalLink && (
            <a
              href={field.externalLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {field.externalLink.label}
              <HugeiconsIcon icon={LinkSquare01Icon} size={10} />
            </a>
          )}
        </div>
        {isLoadingField ? (
          <Skeleton className="h-9 w-full" />
        ) : field.type === 'readonly' ? (
          <div
            className={cn(
              'flex items-center gap-2 p-2.5 rounded-md border bg-muted/50 cursor-pointer hover:bg-muted transition-colors text-sm',
              field.monospace && 'font-mono text-xs'
            )}
            onClick={() => fieldValue && handleCopy(fieldValue, field.id)}
          >
            <span className="flex-1 truncate text-muted-foreground">{fieldValue || field.placeholder}</span>
            <HugeiconsIcon
              icon={copiedField === field.id ? Tick01Icon : Copy01Icon}
              size={14}
              className={cn('shrink-0', copiedField === field.id ? 'text-success' : 'text-muted-foreground')}
            />
          </div>
        ) : (
          <Input
            id={field.id}
            type={field.type === 'password' ? 'password' : 'text'}
            placeholder={field.placeholder}
            value={fieldValue}
            onChange={e => handleChange(field.id, e.target.value)}
            className={cn('h-9', field.monospace && 'font-mono text-xs')}
          />
        )}
        {field.helpText && <p className="text-[11px] text-muted-foreground">{field.helpText}</p>}
      </div>
    )
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${brandColor}15` }}
              >
                <BrandIcon brand={config.brandKey} className="h-5 w-5" />
              </div>
              <div>
                <SheetTitle className="text-base">{config.name}</SheetTitle>
                <SheetDescription className="text-xs">{config.description}</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {isLoadingConfig ? (
            <div className="mt-6 space-y-4">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-8 w-24" />
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              {isFormsType && (
                <p className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/20 border border-border/30">
                  The tracking script already records every form submission. This connection only reconciles {config.name} with those sessions.
                </p>
              )}

              {/* Status indicator */}
              {isConnected && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/20 border border-border/30">
                  <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-success dark:text-success shrink-0" />
                  <p className="text-sm text-success dark:text-success">
                    {isWebhookOnlyType ? 'Webhook endpoint active' : `${config.name} is connected`}
                  </p>
                </div>
              )}

              {/* Revenue display toggle */}
              {isRevenueType && isConnected && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-border/30 bg-muted/20">
                  <div>
                    <p className="text-sm font-medium">Show on Dashboard</p>
                    <p className="text-[11px] text-muted-foreground">Display revenue data in analytics</p>
                  </div>
                  <Switch
                    checked={showRevenue}
                    onCheckedChange={handleToggleRevenue}
                    disabled={isTogglingRevenue}
                  />
                </div>
              )}

              {isRevenueType && (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground">Workspace</Label>
                  <p className="text-sm">{workspaceLabel}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Revenue is attributed across every site in this workspace.
                  </p>
                </div>
              )}

              <Separator />

              {/* Form Fields */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-foreground">
                  {isWebhookOnlyType ? 'Webhook Configuration' : isConnected ? 'Update Configuration' : 'Configuration'}
                </h3>
                {config.fields.map(renderField)}
              </div>

              {/* Setup Instructions */}
              {config.helpContent.setupSteps && config.helpContent.setupSteps.length > 0 && (
                <Collapsible open={setupOpen} onOpenChange={setSetupOpen}>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-foreground w-full">
                    <HugeiconsIcon
                      icon={ArrowDown01Icon}
                      size={12}
                      className={cn('transition-transform', !setupOpen && '-rotate-90')}
                    />
                    Setup instructions
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <div className="p-3 bg-muted/30 rounded-lg border border-border/30 space-y-2">
                      {config.helpContent.setupSteps.map((step, i) => (
                        <div key={i} className="flex gap-2.5 text-sm">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-medium text-primary">
                            {i + 1}
                          </span>
                          <span className="text-muted-foreground text-xs leading-5">{step}</span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Error / Success */}
              {error && (
                <Alert variant="destructive">
                  <HugeiconsIcon icon={Alert01Icon} size={14} />
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}
              {testSuccess && (
                <Alert className="border-success/50 bg-success-tint dark:bg-success-tint">
                  <HugeiconsIcon icon={Tick01Icon} size={14} className="text-success dark:text-success" />
                  <AlertDescription className="text-xs text-success dark:text-success">
                    Connection test passed!
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              {isWebhookOnlyType ? (
                <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
                  Done
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button
                    className="w-full"
                    onClick={handleSave}
                    disabled={isLoading || !hasChanges}
                  >
                    {isLoading ? 'Saving...' : isConnected ? 'Save Changes' : `Connect ${config.name}`}
                  </Button>
                  {isConnected && config.supportsTest && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleTest}
                      disabled={isTesting}
                    >
                      {isTesting ? 'Testing...' : 'Test Connection'}
                    </Button>
                  )}
                </div>
              )}

              {/* Resources */}
              {(config.helpContent.bullets.length > 0 || config.helpContent.docsUrl) && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-foreground">Resources</h3>
                    {config.helpContent.bullets.map((bullet, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                        <span className="text-xs text-muted-foreground">{bullet}</span>
                      </div>
                    ))}
                    {config.helpContent.docsUrl && (
                      <a
                        href={config.helpContent.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <HugeiconsIcon icon={LinkSquare01Icon} size={12} />
                        {config.helpContent.docsLabel || 'Documentation'}
                      </a>
                    )}
                  </div>
                </>
              )}

              {/* Danger Zone */}
              {isConnected && isRevenueType && (
                <>
                  <Separator />
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors">
                      <HugeiconsIcon icon={ArrowDown01Icon} size={12} />
                      Danger zone
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3">
                      <p className="text-xs text-muted-foreground mb-3">
                        Permanently remove this integration. Existing data is preserved.
                      </p>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full"
                        onClick={() => setDisconnectOpen(true)}
                      >
                        <HugeiconsIcon icon={Delete01Icon} size={14} className="mr-1.5" />
                        Disconnect {config.name}
                      </Button>
                    </CollapsibleContent>
                  </Collapsible>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {config.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {config.name} integration from {workspaceLabel} and stop tracking revenue.
              Existing data will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
